/**
 * The dropdown for the task builder — nine raw `<select>` elements had no shared home.
 *
 * A native select on purpose: it is a closed list of short options, and the platform control is
 * keyboard- and screen-reader-correct for free. The custom button+listbox pattern in the blocks
 * exists for two cases this deliberately does not serve — trigger pickers, which need evaluation
 * chips and colour, and the student-facing Dropdown answer control, whose options carry rich HTML
 * (a native `<option>` can only show plain text, which turns `2H2 + O2 -> 2H2O` into a wall of
 * escaped characters). A plain choice needs neither.
 *
 * Draws its own chevron with `appearance-none` rather than leaving the platform arrow, because the
 * blocks were already doing that by hand in at least one place and a mix of native and custom
 * arrows in one panel reads as two kinds of control.
 */
import { ChevronDown } from 'lucide-react';

import type { KeyboardEventHandler } from 'react';

export interface SelectFieldOption { value: string; label: string; disabled?: boolean; }

export interface SelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectFieldOption[];
  /** This choice is invalid now — a target task that no longer exists, say. */
  hasError?: boolean;
  /** Softer: the choice is usable but questionable. Outranked by `hasError`. */
  hasWarning?: boolean;
  /** Registers the control as a target for `focusIssueField`. */
  fieldId?: string;
  /** For a select that already has a real <label htmlFor>. */
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /**
   * Borderless and inline, for a select sitting inside a sentence rather than on a form row.
   *
   * Borderless, NOT quiet. It was italic and `ws-muted`, which made the three choices in Flow's
   * attempt condition — the only thing on that row a teacher actually sets — read as grey
   * commentary about a sentence rather than as the sentence's own words. The chevron stays muted:
   * that is chrome, and it is the one part not being chosen.
   */
  inline?: boolean;
  /**
   * For a select inside a widget with its own key handling — the sound player stops propagation
   * so its transport shortcuts do not fire while the dropdown has focus.
   */
  onKeyDown?: KeyboardEventHandler<HTMLSelectElement>;
  className?: string;
}

export function SelectField({
  value, onChange, options, hasError, hasWarning, fieldId, id, ariaLabel, disabled,
  inline = false, className = '', onKeyDown,
}: SelectFieldProps) {
  const tone = hasError ? 'border-ws-error-fg text-ws-error-fg'
    : hasWarning ? 'border-ws-warning-fg text-ws-warning-fg'
    : 'border-ws-border text-ws-text';
  const box = inline
    ? 'truncate rounded bg-transparent pr-5 text-xs font-medium text-ws-text hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
    : `rounded-md border bg-ws-btn pl-2 pr-7 py-1.5 text-sm ${tone}`;
  return (
    <span className={`relative inline-flex items-center ${className}`}>
      <select
        data-field-kind='select'
        data-field-id={fieldId}
        id={id}
        aria-label={ariaLabel}
        aria-invalid={hasError || undefined}
        disabled={disabled}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        className={`w-full appearance-none outline-none disabled:cursor-not-allowed disabled:opacity-40 ${box}`}
      >
        {options.map(o => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
      </select>
      <ChevronDown
        aria-hidden='true'
        className={`pointer-events-none absolute right-2 h-3.5 w-3.5 ${inline ? 'text-ws-muted' : 'opacity-60'}`}
      />
    </span>
  );
}
